import { Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { NotificationsService } from 'src/app/_services/notifications/notifications.service';
import { Notification } from 'src/app/interfaces/notification.interfaces';
import { AuthService } from 'src/app/_services/auth/auth.service';

/**
 * Componente de menú desplegable de notificaciones que recibe alertas en
 * tiempo real (SSE), muestra las no leídas y permite marcarlas como leídas
 * de forma individual o masiva.
 */
@Component({
  selector: 'app-notification-dropdown',
  templateUrl: './notification-dropdown.component.html',
  styleUrls: ['./notification-dropdown.component.css']
})
export class NotificationDropdownComponent implements OnInit, OnDestroy {
  isOpen = false;
  notifications: Notification[] = [];
  isLoading = false;
  hasPermission = false;
  private sseSubscription?: Subscription;

  constructor(
    private router: Router,
    private elementRef: ElementRef,
    private notificationsService: NotificationsService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.hasPermission = this.authService.hasAuthorization('notifications', 'actions', 'notifications');

    if (this.hasPermission) {
      this.subscribeToNotifications();
    }
  }

  private subscribeToNotifications(): void {
    this.sseSubscription = this.notificationsService.getLatestUnreadNotifications().subscribe({
      next: (notification) => {
        this.notifications.unshift(notification);
        if (this.notifications.length > 10) {
          this.notifications = this.notifications.slice(0, 10);
        }
      },
      error: (error) => {
        console.error('Error en SSE de notificaciones:', error);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.sseSubscription) {
      this.sseSubscription.unsubscribe();
    }
  }

  get unreadNotifications(): Notification[] {
    return this.notifications.filter(n => !n.is_read).slice(0, 10);
  }

  get unreadCount(): number {
    return this.notifications.filter(n => !n.is_read).length;
  }

  /**
   * Alterna la visibilidad del menú desplegable de notificaciones.
   */
  toggle(): void {
    this.isOpen = !this.isOpen;
  }

  /**
   * Marca una notificación como leída, revirtiendo el cambio si el servidor responde con error.
   * @param event - Evento del DOM cuya propagación se detiene.
   * @param notification - Notificación que se marca como leída.
   */
  markAsRead(event: Event, notification: Notification): void {
    event.stopPropagation();
    const previousState = notification.is_read;
    notification.is_read = true;

    this.notificationsService.markAsRead(notification.id, true).subscribe({
      next: (response: any) => {
        if (response.error) {
          console.error('Error al marcar como leída:', response.message);
          notification.is_read = previousState;
        }
      },
      error: (err) => {
        console.error('Error al marcar como leída:', err);
        notification.is_read = previousState;
      }
    });
  }

  /**
   * Marca todas las notificaciones como leídas y vacía la lista al completarse.
   * @param event - Evento del DOM cuya propagación se detiene.
   */
  markAllAsRead(event: Event): void {
    event.stopPropagation();
    this.isLoading = true;
    this.notificationsService.markAllAsRead().subscribe({
      next: (response: any) => {
        if (response.error) {
          console.error('Error al marcar todas como leídas:', response.message);
        } else {
          this.notifications = [];
        }
      },
      error: (err) => {
        console.error('Error al marcar todas como leídas:', err);
      },
      complete: () => this.isLoading = false
    });
  }

  /**
   * Cierra el menú y navega al centro de notificaciones.
   */
  goToNotificationCenter(): void {
    this.isOpen = false;
    this.router.navigate(['/notification/list']);
  }

  /**
   * Cierra el menú desplegable cuando se hace clic fuera del componente.
   * @param event - Evento de clic del documento.
   */
  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }
}
